import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import useAuth from 'app/hook/common/useAuth';
import useTrackingDataStory from 'app/hook/common/useTrackingDataStory';
import useTrackingEnlog from 'app/hook/common/useTrackingEnlog';
import { AsideMenu, useAsideMenu, Header, Footer, PcFooter } from '@encarpkg/design';
import usePage from 'app/hook/common/usePage';
import PcHeader from 'app/components/common/PcHeader';
import NotySiteMaintenance from 'app/components/common/NotySiteMaintenance';
import NotyTimezoneAlert from 'app/components/common/NotyTimezoneAlert';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';

import classNames from 'classnames/bind';
import styles from './Layout.module.scss';

const cx = classNames.bind(styles);

function Layout({
	title,
	usePrevBtn = false,
	useCloseBtn = false,
	onPrev = undefined,
	useHeaderLogo = false,
	onClose = undefined,
	children = null,
	headerFixed = false,
	hide = false,
	headerType = null,
	contentsType = '',
	useHeader = true,
	useFooter = false,
	footerType = '',
	useAside = false,
	useMyBtn = false,
	useCloseClass = '',
	useRightBtn = false,
	useRightClass = null,
	useRightName = '',
	useRightColor = '',
	useReloadBtn = false,
	onRight = undefined,
	usePc = false,
	checkAuth = false,
	isPassAuth = undefined,
	authToHome = false,
	unAuthToLogin = false,
	destroyWebViewAfterLogin = false,
	useHeaderTitle = true,
	useServiceLink = false,
	serviceLink = '',
	useAlertCheckAuth = false,
	scrollRestoration = true,
	style = null,
	usePageHook = false,
	dataStoryAutoLog = true,
	headerBgColor = '',
	useHeaderItemWhite = false,
	viewMode = null,
	pcLayout = {
		header: null,
		footer: null,
		layoutClass: 'mini',
		contentClass: null,
		headerInfo: {
			type: 'a',
			titleText: '',
			gnbActiveKey: null,
			snbActiveKey: null,
			snbData: [],
			viewUtil: true,
			login: {
				isLoggedIn: null,
				userInfo: {},
			},
		},
	},
	pcSubMenuInfo = null,
	pcTitle = null,
	useSSR = false,
	usePcLogoNoLink = false,
	htmlLang = '',
	htmlClass = '',
	useHeaderLine = false,
	bodyClass = '',
	bodyStyle = '',
	headerSub = false,
	useBottomFix = false,
	enlogConfig = null,
	useShare = false,
	pcHeaderStyle = null,
	prevBtnStyle = {},
	closeBtnStyle = {},
	closeBtnProps = {},
	rightBtnStyle = {},
	asideActiveTab = 'buy',
	headerTitleTextStyle = {},
	contentsStyle = {},
}) {
	const ua = useSsrUserAgent();
	const { pathname } = useLocation();
	const { initAuth, getUser, isLoggedIn, userInfo } = useAuth();
	const { view, handleAsideOpen, handleAsideClose } = useAsideMenu();
	const [headerFix, setHeaderFix] = useState(false);

	const headerPropsGroup = {
		title,
		useAsideBtn: useAside,
		usePrevBtn,
		useCloseBtn,
		useCloseClass,
		useHeaderLogo,
		useMyBtn,
		useRightBtn,
		useRightClass,
		useRightName,
		useRightColor,
		useReloadBtn,
		onAside: handleAsideOpen,
		onPrev,
		onClose,
		onRight,
		headerFixed,
		headerType,
		useHeaderTitle,
		useServiceLink,
		serviceLink,
		headerBgColor,
		useHeaderItemWhite,
		headerSub,
		useHeaderLine,
		useShare,
		setHeaderFix,
		prevBtnStyle,
		closeBtnStyle,
		closeBtnProps,
		rightBtnStyle,
		asideActiveTab,
		headerTitleTextStyle,
	};

	const pcHeaderPropsGroup = {
		title,
		pcHeaderType: pcLayout.header,
		pcSubMenuInfo,
		pcTitle,
		usePcLogoNoLink,
		style: pcHeaderStyle,
		pcHeaderInfo: {
			...pcLayout.headerInfo,
			...{ titleText: pcLayout.headerInfo?.titleText || pcTitle || title },
			login: {
				isLoggedIn,
				userInfo,
			},
		},
	};
	const isLayoutMini = usePc && pcLayout.layoutClass === 'mini';
	const isMobileLook = pcLayout.layoutClass === 'car_detail' ? viewMode === 'mobileMode' : !ua().isPc || isLayoutMini;
	const pcCommonClass = usePc && ua().isPc && !isMobileLook ? 'pc_look' : null;
	let layoutClass = '';
	// 차량상세는 유저가 선택한 화면(pc 또는 mobile)을 우선시 함
	if (pcLayout.layoutClass === 'car_detail' && viewMode === 'pcMode') {
		layoutClass = `pclayout_${pcLayout.layoutClass}`;
	} else if (pcLayout.layoutClass === 'car_detail' && viewMode === 'mobileMode') {
		layoutClass = `mobilelayout_${pcLayout.layoutClass}`;
	} else {
		layoutClass = usePc && ua().isPc && pcLayout.layoutClass ? `pclayout_${pcLayout.layoutClass}` : `mobilelayout_${pcLayout.layoutClass}`;
	}
	const pcContentClass = pcLayout.contentClass !== undefined ? `pccontent_${pcLayout.contentClass}` : null;
	const contentClass = usePc && ua().isPc && !isMobileLook ? pcContentClass : contentsType;

	useEffect(() => {
		const passAuth = isPassAuth != null ? isPassAuth : (useAside || useFooter) && !checkAuth;
		const check = checkAuth || passAuth;
		const authOptions = {
			authToHome,
			unAuthToLogin,
			useAlertCheckAuth,
			destroyWebViewAfterLogin,
		};
		getUser({
			checkAuth: check,
			passAuth,
			authOptions,
			destroyWebViewAfterLogin,
		});
	}, [getUser, isPassAuth, checkAuth, useAside, useFooter, authToHome, unAuthToLogin, useAlertCheckAuth, destroyWebViewAfterLogin]);

	// usePage
	const { hideLayout } = usePage();

	// 페이지로드시 지표 실행
	useTrackingDataStory(dataStoryAutoLog);
	useTrackingEnlog(enlogConfig);

	// Scroll Restoration
	useEffect(() => {
		if (scrollRestoration && !usePageHook) window.scrollTo(0, 0);
	}, [pathname, scrollRestoration, usePageHook]);

	if (!initAuth && !useSSR) {
		const headInfoComponent = (Array.isArray(children) ? children : [children]).find((c) => c?.type?.displayName === 'HeadInfo');
		return headInfoComponent ?? null;
	}

	return (
		<>
			<Helmet>
				{(htmlLang || pcCommonClass || layoutClass || htmlClass || useBottomFix) && (
					<html
						lang={htmlLang !== '' ? htmlLang : 'ko'}
						className={cx(pcCommonClass, layoutClass, htmlClass, { bottom_fix: useBottomFix })}
					/>
				)}
				{(bodyClass || bodyStyle) && (
					<body
						className={bodyClass}
						style={bodyStyle}
					/>
				)}
			</Helmet>
			<div
				className={cx('wrap_contents', { header_fixed: headerFix }, { hide: hide || (!usePageHook && hideLayout) })}
				style={style}
			>
				{useHeader && <>{isMobileLook || !usePc ? <Header {...headerPropsGroup} /> : <PcHeader {...pcHeaderPropsGroup} />}</>}

				{useAside && (isMobileLook || !usePc) && (
					<AsideMenu
						view={view}
						onClose={handleAsideClose}
						isLoggedIn={isLoggedIn}
						userInfo={userInfo}
						asideActiveTab={asideActiveTab}
					/>
				)}

				<div
					className={cx('contents', contentClass)}
					style={contentsStyle}
				>
					{children}
				</div>

				{useFooter && (isMobileLook || !usePc) && (
					<Footer
						footerType={footerType}
						isLoggedIn={isLoggedIn}
					/>
				)}
				{!isMobileLook && usePc && (useFooter || (pcLayout.layoutClass === 'car_detail' && viewMode === 'pcMode')) && (
					<PcFooter pcfooterType={pcLayout.footer} />
				)}
			</div>
			<NotySiteMaintenance dataName="NotySiteMaintenance" />
			<NotyTimezoneAlert dataName="NotyTimezoneAlert" />
		</>
	);
}

export default Layout;
